import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PROPERTY_TYPES, US_STATES } from "@/lib/constants";
import { Building2, MapPin, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", street_address: "", unit_number: "", city: "", state: "", zip_code: "", property_type: "", occupancy: "Occupied", tenant_name: "", tenant_phone: "", tenant_email: "", access_instructions: "", gate_code: "", lockbox_code: "", notes: "" };

export function PropertyForm({ onSaved }) {
  const { user } = useAuth();
  const [f, setF] = useState({ ...EMPTY, city: user?.city || "", state: user?.state || "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target?.value ?? e });

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/properties", f);
      toast.success("Property added");
      onSaved(data);
    } catch (err) { toast.error("Failed to add property"); }
    setLoading(false);
  };

  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2"><Label>Property Name</Label><Input data-testid="prop-name" required value={f.name} onChange={set("name")} className="mt-1.5" /></div>
      <div className="sm:col-span-2"><Label>Street Address</Label><Input data-testid="prop-street" required value={f.street_address} onChange={set("street_address")} className="mt-1.5" /></div>
      <div><Label>Unit Number</Label><Input data-testid="prop-unit" value={f.unit_number} onChange={set("unit_number")} className="mt-1.5" /></div>
      <div><Label>ZIP Code</Label><Input data-testid="prop-zip" required value={f.zip_code} onChange={set("zip_code")} className="mt-1.5" /></div>
      <div><Label>City</Label><Input data-testid="prop-city" required value={f.city} onChange={set("city")} className="mt-1.5" /></div>
      <div>
        <Label>State</Label>
        <Select value={f.state} onValueChange={set("state")}>
          <SelectTrigger data-testid="prop-state" className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent className="max-h-56">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Property Type</Label>
        <Select value={f.property_type} onValueChange={set("property_type")}>
          <SelectTrigger data-testid="prop-type" className="mt-1.5"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>{PROPERTY_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Occupancy</Label>
        <Select value={f.occupancy} onValueChange={set("occupancy")}>
          <SelectTrigger data-testid="prop-occupancy" className="mt-1.5"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="Occupied">Occupied</SelectItem><SelectItem value="Vacant">Vacant</SelectItem></SelectContent>
        </Select>
      </div>
      <div><Label>Tenant Name</Label><Input data-testid="prop-tenant-name" value={f.tenant_name} onChange={set("tenant_name")} className="mt-1.5" /></div>
      <div><Label>Tenant Phone</Label><Input data-testid="prop-tenant-phone" value={f.tenant_phone} onChange={set("tenant_phone")} className="mt-1.5" /></div>
      <div className="sm:col-span-2"><Label>Tenant Email</Label><Input data-testid="prop-tenant-email" value={f.tenant_email} onChange={set("tenant_email")} className="mt-1.5" /></div>
      <div><Label>Gate Code</Label><Input data-testid="prop-gate" value={f.gate_code} onChange={set("gate_code")} className="mt-1.5" /></div>
      <div><Label>Lockbox Code</Label><Input data-testid="prop-lockbox" value={f.lockbox_code} onChange={set("lockbox_code")} className="mt-1.5" /></div>
      <div className="sm:col-span-2"><Label>Access Instructions</Label><Textarea data-testid="prop-access" value={f.access_instructions} onChange={set("access_instructions")} className="mt-1.5" /></div>
      <div className="sm:col-span-2"><Label>Property Notes</Label><Textarea data-testid="prop-notes" value={f.notes} onChange={set("notes")} className="mt-1.5" /></div>
      <div className="sm:col-span-2"><Button data-testid="prop-save" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 h-11">{loading ? "Saving..." : "Save Property"}</Button></div>
    </form>
  );
}

export default function Properties() {
  const navigate = useNavigate();
  const { user, isViewer } = useAuth();
  const [props, setProps] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/properties").then(({ data }) => setProps(data)).catch(() => setProps([]));
  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout nav={getNav("client", user?.org_role)} title="Properties">
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-500 text-sm">{props?.length || 0} propert{props?.length === 1 ? "y" : "ies"}</p>
        {!isViewer && <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-property-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> Add Property</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-head">Add Property</DialogTitle></DialogHeader>
            <PropertyForm onSaved={() => { setOpen(false); load(); }} />
          </DialogContent>
        </Dialog>}
      </div>

      {!props ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        props.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <Building2 className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-500">No properties yet. Add your first property.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {props.map((p) => (
              <button key={p.id} data-testid={`property-card-${p.id}`} onClick={() => navigate(`/app/client/properties/${p.id}`)}
                className="group text-left bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md hover:border-blue-300 transition-all">
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Building2 className="w-5 h-5 text-blue-600" /></div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.occupancy === "Vacant" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{p.occupancy}</span>
                  </div>
                  <h3 className="mt-3 font-head font-semibold text-slate-900">{p.name}</h3>
                  <p className="mt-1 text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.street_address}{p.unit_number ? `, #${p.unit_number}` : ""}</p>
                  <p className="text-sm text-slate-500">{p.city}, {p.state} {p.zip_code}</p>
                  <p className="mt-2 text-xs font-medium text-blue-600">{p.property_type}</p>
                </div>
              </button>
            ))}
          </div>
        )}
    </DashboardLayout>
  );
}
